import { Badge, Group, Stack, Text } from "@mantine/core"
import { CertificateStatus } from "@tfg/db"
import { Missing } from "@tfg/ui"
import { format } from "date-fns"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { DocumentActionsCell } from "~/components/cells/DocumentActionsCell.tsx"
import { FindCertificatesByOrganizationIdResponseDto } from "~/gen/api"
import { CertificateStatusBadge } from "~/pages/Brand/Certificates/components/CertificateStatusBadge.tsx"
import {
  filterCertificatesWithTemplate,
  FilteredCertificate,
  getCertificateTemplate,
} from "~/pages/Brand/Certificates/components/templates.ts"
import { certificateIsVerifiedWithOldRules } from "~/pages/Brand/Products/List/Services/standardCompliance.tsx"

type Props = {
  certificate:
    | FindCertificatesByOrganizationIdResponseDto
    | AdminResponses.Certificate
  showDocumentIcons?: boolean
  maxListElements?: number
}

export const CertificateSummary = ({
  certificate,
  showDocumentIcons,
}: Props) => {
  const { t } = useTranslation("certificates")

  const filteredCertificates = filterCertificatesWithTemplate(
    certificate,
    getCertificateTemplate(certificate.type.category)
  )

  const showLongSummary = !certificateIsVerifiedWithOldRules(certificate)

  if (!filteredCertificates) return null

  return (
    <Stack>
      <Group>
        <Group w="100%" justify="space-between" wrap="nowrap">
          <InfoItem
            label={t("modal.labels.name")}
            value={<TextValue value={filteredCertificates.name} />}
          />
          {showDocumentIcons && certificate.documentId && (
            <DocumentActionsCell
              documentId={certificate.documentId}
              documentName={certificate.name}
            />
          )}
        </Group>
        <Group wrap="nowrap">
          {filteredCertificates.status && (
            <InfoItem
              label={t("modal.labels.status")}
              value={
                <CertificateStatusBadge status={filteredCertificates.status} />
              }
            />
          )}
          {filteredCertificates.type && (
            <InfoItem
              label={t("modal.labels.type")}
              value={<TextValue value={filteredCertificates.type.name} />}
            />
          )}
        </Group>
        <Group wrap="nowrap">
          <InfoItem
            label={t("modal.labels.issueDate")}
            value={
              filteredCertificates.issueDate && (
                <TextValue
                  value={format(
                    new Date(filteredCertificates.issueDate),
                    "MM/dd/yyyy"
                  )}
                />
              )
            }
          />
          {"expiryDate" in filteredCertificates && (
            <InfoItem
              label={t("modal.labels.expiryDate")}
              value={
                filteredCertificates.expiryDate && (
                  <TextValue
                    value={format(
                      new Date(filteredCertificates.expiryDate),
                      "MM/dd/yyyy"
                    )}
                  />
                )
              }
            />
          )}
        </Group>
        <InfoItem
          label={t("modal.labels.reference")}
          value={<TextValue value={filteredCertificates.reference} />}
        />
        {"score" in filteredCertificates && (
          <InfoItem
            label={t("modal.labels.score")}
            value={<TextValue value={filteredCertificates.score} />}
          />
        )}
        {showLongSummary && (
          <LongSummaryFields filteredCertificates={filteredCertificates} />
        )}
        {filteredCertificates.status === CertificateStatus.REJECTED &&
          filteredCertificates.rejectionMessage && (
            <InfoItem
              w="100%"
              label={t("modal.labels.rejectionMessage")}
              value={
                <TextValue value={filteredCertificates.rejectionMessage} />
              }
            />
          )}
      </Group>
    </Stack>
  )
}

type InfoItemProps = {
  label: string
  value: ReactNode | null
  w?: string
}

export const InfoItem = ({ label, value, ...otherProps }: InfoItemProps) => {
  return (
    <Stack gap="5px" {...otherProps}>
      <Text miw={150}>{label}</Text>
      {value ? value : <Missing />}
    </Stack>
  )
}

type LongSummaryFieldsProps = {
  filteredCertificates: FilteredCertificate
}

function LongSummaryFields({ filteredCertificates }: LongSummaryFieldsProps) {
  const { t } = useTranslation("certificates")

  return (
    <>
      {"certificationBody" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.certificationBody")}
          value={<TextValue value={filteredCertificates.certificationBody} />}
        />
      )}
      {"certifiedCompanyLicenseNumber" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.certifiedCompanyLicenseNumber")}
          value={
            <TextValue
              value={filteredCertificates.certifiedCompanyLicenseNumber}
            />
          }
        />
      )}
      {"certifiedCompanyName" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.certifiedCompanyName")}
          value={
            <TextValue value={filteredCertificates.certifiedCompanyName} />
          }
        />
      )}
      {"certifiedCompanyAddress" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.certifiedCompanyName")}
          value={
            <TextValue value={filteredCertificates.certifiedCompanyAddress} />
          }
        />
      )}
      {"productCategories" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.productCategories")}
          value={
            <TextValue
              value={filteredCertificates.productCategories}
              maxListElements={5}
            />
          }
        />
      )}
      {"processingSteps" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.processingSteps")}
          value={
            <TextValue
              value={filteredCertificates.processingSteps}
              maxListElements={5}
            />
          }
        />
      )}
      {"productDetails" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.productDetails")}
          value={
            <TextValue
              value={filteredCertificates.productDetails}
              maxListElements={5}
            />
          }
        />
      )}
      {"buyerOfCertifiedProducts" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.buyerOfCertifiedProducts")}
          value={
            <TextValue value={filteredCertificates.buyerOfCertifiedProducts} />
          }
        />
      )}
      {"certifiedWeight" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.certifiedWeight")}
          value={<TextValue value={filteredCertificates.certifiedWeight} />}
        />
      )}
      {"linkedScopeCertificateNumber" in filteredCertificates && (
        <InfoItem
          label={t("modal.labels.linkedScopeCertificateNumber")}
          value={
            <TextValue
              value={filteredCertificates.linkedScopeCertificateNumber}
            />
          }
        />
      )}
    </>
  )
}

export function TextValue({
  value,
  maxListElements,
}: {
  value: string | null | undefined | string[]
  maxListElements?: number
}) {
  if (!value) {
    return <Missing />
  }

  if (Array.isArray(value)) {
    const initialLength = value.length
    if (maxListElements) value = value.slice(0, maxListElements)

    return (
      <Group gap="xs">
        {value.map((v, index) => (
          <Badge size="xs" key={index}>
            {v}
          </Badge>
        ))}
        {maxListElements && initialLength > maxListElements && (
          <Badge size="xs">...</Badge>
        )}
      </Group>
    )
  }

  return <Text fw="bold">{value}</Text>
}
